/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { data, type ObservableValue } from '@amazon/vinyl-observable'
import { logDebug, logWarn } from '@amazon/vinyl-util'
import type { AgentCard } from '@/blackbolt/cardTypes'
import { toastDismissFailure } from '@/blackbolt/actionToasts'
import { ctx } from '@/util/context'
import { globalData } from '@amzn/bf-playback'

const { target } = ctx('dismissStack')

/** Default lifetime of a dismiss / undo entry in ms. Toast auto-dismisses in the same window. */
export const DISMISS_TTL_MS = 5000

/**
 * A single "you can still undo this" entry. The full `AgentCard` is
 * stashed under `card` so Undo re-inserts exactly what was on screen,
 * even if pagination dropped it from the feed meanwhile.
 */
export interface DismissEntry {
    readonly cardId: string
    readonly prevIndex: number
    readonly ttl: number
    readonly expiresAt: number
    readonly card: AgentCard
}

// Newest entry is at the TAIL (`at(-1)`) so `UndoToast` can read "the
// most recent" dismiss without walking the list.
const stack = globalData(() => data<readonly DismissEntry[]>([]))
export const dismissStack: ObservableValue<readonly DismissEntry[]> = stack

/**
 * Narrow slice of the feed the dismiss module needs. Going through
 * thunks keeps the coupling one-directional: `Feed.tsx` owns the
 * observable; this module only reads / writes through the controller.
 */
export interface DismissFeedController {
    readonly getCards: () => readonly AgentCard[]
    readonly setCards: (next: readonly AgentCard[]) => void
}

let controller: DismissFeedController | null = null

/** Call from `Feed.tsx`'s onConnect; clear on the disposer. */
export function setDismissController(next: DismissFeedController | null): void {
    controller = next
}

/**
 * Dismiss a card: remove it from the feed, push a `DismissEntry`, and
 * schedule TTL eviction. No-op (returns `null`) when the id isn't in the
 * current cards array.
 */
export function dismissCard(cardId: string, ttl: number = DISMISS_TTL_MS): DismissEntry | null {
    if (!controller) {
        logWarn(target, 'dismissCard called with no feed controller wired', { cardId })
        return null
    }
    const cards = controller.getCards()
    const prevIndex = cards.findIndex((c) => c.id === cardId)
    if (prevIndex < 0) {
        logWarn(target, 'dismissCard: card not in current feed', { cardId })
        return null
    }
    const card = cards[prevIndex]
    const next = cards.slice(0, prevIndex).concat(cards.slice(prevIndex + 1))
    controller.setCards(next)

    const entry: DismissEntry = {
        cardId,
        prevIndex,
        ttl,
        expiresAt: Date.now() + ttl,
        card,
    }
    stack.value = stack.value.concat([entry])
    logDebug(target, 'dismissCard', { cardId, prevIndex })

    // Evict by `entry` identity (not `cardId`) so a fast repeat dismiss
    // of the same card can't have the older timer knock out the newer.
    scheduleEvict(entry, ttl)
    return entry
}

/**
 * Undo the most-recent dismiss: re-insert the card at its original
 * position (clamped to the current length so a shrunken list can't
 * produce a hole) and pop the entry. No-op if the stack is empty.
 */
export function undoLast(): DismissEntry | null {
    const current = stack.value
    if (current.length === 0) return null
    const entry = current[current.length - 1]
    if (!controller) {
        logWarn(target, 'undoLast called with no feed controller wired', { cardId: entry.cardId })
        // Drop the entry anyway so the stale toast dismisses cleanly.
        stack.value = current.slice(0, -1)
        // The user's undo click had no visible effect — surface the
        // shared failure toast so the click doesn't silently vanish.
        toastDismissFailure()
        return entry
    }
    const cards = controller.getCards()
    // Clamp to `cards.length` so a shorter (paginated / refreshed) feed
    // appends at the tail instead of producing a sparse array.
    const at = Math.min(Math.max(0, entry.prevIndex), cards.length)
    const next = cards.slice(0, at).concat([entry.card]).concat(cards.slice(at))
    controller.setCards(next)
    stack.value = current.slice(0, -1)
    logDebug(target, 'undoLast', { cardId: entry.cardId, prevIndex: entry.prevIndex, at })
    return entry
}

const scheduled = new Map<DismissEntry, ReturnType<typeof setTimeout>>()

function scheduleEvict(entry: DismissEntry, ttl: number): void {
    const timer = setTimeout(() => {
        scheduled.delete(entry)
        // Skip if a successful Undo / reset already dropped this entry.
        // `indexOf` matches by identity so a repeat-dismiss of the same
        // cardId can't knock out the newer entry.
        const current = stack.value
        const idx = current.indexOf(entry)
        if (idx < 0) return
        stack.value = current.slice(0, idx).concat(current.slice(idx + 1))
    }, ttl)
    scheduled.set(entry, timer)
}
